import React, { useMemo, useState } from 'react'
import { Alert, Box, Grid, Stack, TextField, Typography } from '@mui/material'
import MerchantToolShell, { shellStyles } from './MerchantToolShell'
import { useLanguage } from '../../contexts/LanguageContext'
import {
    createMerchantTaxRemittance,
    getMerchantTaxRemittances,
} from '../../utils/merchantToolStorage'

const countryOptions = ['Sierra Leone', 'Liberia', 'Guinea', 'Gambia']
const taxTypeOptions = [
    { en: 'Local / City Tax', fr: 'Taxe locale / municipale' },
    { en: 'Business License', fr: 'Licence commerciale' },
    { en: 'Market Dues', fr: 'Droits de marche' },
    { en: 'Government Tax', fr: 'Taxe d Etat' },
]
const frequencyOptions = [
    { en: 'Daily', fr: 'Journalier' },
    { en: 'Monthly', fr: 'Mensuel' },
    { en: 'Annual', fr: 'Annuel' },
]

export default function MerchantBusinessTax() {
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const [country, setCountry] = useState('Sierra Leone')
    const [taxType, setTaxType] = useState('Local / City Tax')
    const [frequency, setFrequency] = useState('Daily')
    const [reference, setReference] = useState('')
    const [amount, setAmount] = useState('')
    const [receipt, setReceipt] = useState(null)
    const [error, setError] = useState('')

    const receipts = useMemo(() => getMerchantTaxRemittances(), [])
    const totalCollected = useMemo(
        () => receipts.reduce((sum, item) => sum + Number(item.amount || 0), 0),
        [receipts]
    )
    const todayCollected = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10)
        return receipts
            .filter((item) => String(item.createdAt || '').slice(0, 10) === today)
            .reduce((sum, item) => sum + Number(item.amount || 0), 0)
    }, [receipts])
    const authorityCount = useMemo(
        () => new Set(receipts.map((item) => item.taxType).filter(Boolean)).size,
        [receipts]
    )

    const handleRecordRemittance = () => {
        const parsedAmount = Number(amount || 0)
        if (!(parsedAmount > 0)) {
            setError(isFr ? 'Ajoutez un montant valide.' : 'Add a valid amount.')
            return
        }

        const record = createMerchantTaxRemittance({
            country,
            taxType,
            frequency,
            reference: reference.trim(),
            amount: parsedAmount,
        })

        setReceipt(record)
        setError('')
    }

    const allReceipts = receipt ? [receipt, ...receipts] : receipts

    return (
        <MerchantToolShell
            eyebrow={isFr ? 'Collecte et rapport' : 'Collection and reporting'}
            title={isFr ? 'Collectes fiscales' : 'Tax Collections'}
            subtitle={
                isFr
                    ? 'Suivez ce que votre entreprise a collecte pour l Etat, gardez les recus officiels, puis enregistrez les remises avec preuve.'
                    : 'Track what your business has collected for government, keep official receipts ready, and record remittances with proof.'
            }
            chips={[
                isFr ? 'Taxe collectee' : 'Collected tax',
                isFr ? 'Recus officiels' : 'Official receipts',
                isFr ? 'Remise Etat' : 'Government remittance',
            ]}
            primaryAction={{
                label: isFr ? 'Enregistrer remise' : 'Record Remittance',
                onClick: handleRecordRemittance,
            }}
            left={
                <Stack spacing={2.2}>
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                            <Box sx={shellStyles.softPanel}>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {isFr ? 'Total collecte' : 'Collected Total'}
                                </Typography>
                                <Typography sx={{ fontWeight: 900, fontSize: 26, mt: 0.8 }}>
                                    {totalCollected.toFixed(2)}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.6 }}>
                                    {isFr ? 'Toutes remises enregistrees' : 'All recorded remittances'}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={shellStyles.softPanel}>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {isFr ? 'Aujourd hui' : 'Today'}
                                </Typography>
                                <Typography sx={{ fontWeight: 900, fontSize: 26, mt: 0.8 }}>
                                    {todayCollected.toFixed(2)}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.6 }}>
                                    {isFr ? 'Volume du jour' : 'Daily volume'}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={shellStyles.softPanel}>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {isFr ? 'Recus officiels' : 'Official Receipts'}
                                </Typography>
                                <Typography sx={{ fontWeight: 900, fontSize: 26, mt: 0.8 }}>
                                    {allReceipts.length}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.6 }}>
                                    {isFr ? 'Prets pour audit' : 'Audit ready'}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={shellStyles.softPanel}>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {isFr ? 'Autorites' : 'Authorities'}
                                </Typography>
                                <Typography sx={{ fontWeight: 900, fontSize: 26, mt: 0.8 }}>
                                    {authorityCount}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.6 }}>
                                    {isFr ? 'Types de collecteurs' : 'Collector types'}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 0.8 }}>
                            {isFr ? 'Comment lire cet ecran' : 'How to read this screen'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                            {isFr
                                ? 'Cette zone montre la taxe deja collectee par votre entreprise. Utilisez le formulaire ci-dessous pour enregistrer ce qui doit etre remis au compte du gouvernement.'
                                : 'This area shows tax your business has already collected. Use the form below to record what should be remitted into the government account.'}
                        </Typography>
                    </Box>

                    {error && <Alert severity="error">{error}</Alert>}
                    {receipt && (
                        <Alert severity="success">
                            {isFr
                                ? `Remise enregistree. Recu ${receipt.receiptNumber}.`
                                : `Remittance recorded. Receipt ${receipt.receiptNumber}.`}
                        </Alert>
                    )}

                    <TextField
                        label={isFr ? 'Pays' : 'Country'}
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        select
                        SelectProps={{ native: true }}
                    >
                        {countryOptions.map((item) => (
                            <option key={item} value={item}>
                                {item}
                            </option>
                        ))}
                    </TextField>
                    <TextField
                        label={isFr ? 'Type de collecte' : 'Collection type'}
                        value={taxType}
                        onChange={(e) => setTaxType(e.target.value)}
                        select
                        SelectProps={{ native: true }}
                    >
                        {taxTypeOptions.map((item) => (
                            <option key={item.en} value={item.en}>
                                {isFr ? item.fr : item.en}
                            </option>
                        ))}
                    </TextField>
                    <TextField
                        label={isFr ? 'Frequence de remise' : 'Remittance frequency'}
                        value={frequency}
                        onChange={(e) => setFrequency(e.target.value)}
                        select
                        SelectProps={{ native: true }}
                    >
                        {frequencyOptions.map((item) => (
                            <option key={item.en} value={item.en}>
                                {isFr ? item.fr : item.en}
                            </option>
                        ))}
                    </TextField>
                    <TextField
                        label={isFr ? 'Reference / lot' : 'Reference / batch'}
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                    />
                    <TextField
                        label={isFr ? 'Montant a remettre' : 'Amount to remit'}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                    />
                </Stack>
            }
            right={
                <Stack spacing={2}>
                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>
                            {isFr ? 'Recus officiels recents' : 'Recent Official Receipts'}
                        </Typography>
                        {allReceipts.length === 0 ? (
                            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                {isFr
                                    ? 'Aucune remise ou preuve fiscale enregistree pour le moment.'
                                    : 'No tax remittance or receipt has been recorded yet.'}
                            </Typography>
                        ) : (
                            <Stack spacing={1}>
                                {allReceipts.slice(0, 4).map((item) => (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 3,
                                            bgcolor: 'rgba(91,46,255,0.12)',
                                            border: '1px solid rgba(183,166,255,0.16)',
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 800 }}>
                                            {item.receiptNumber}
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.4 }}>
                                            {item.taxType} • {item.country}
                                        </Typography>
                                        <Typography sx={{ color: '#FF8A00', fontWeight: 800, fontSize: 13, mt: 0.4 }}>
                                            {Number(item.amount).toFixed(2)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Box>

                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>
                            {isFr ? 'Role du gouvernement' : 'Government role'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                            {isFr
                                ? 'Quand le compte gouvernement sera pret, cette meme piste pourra etre verifiee du cote public pour confirmer les remises entrantes.'
                                : 'When the government account is ready, this same trail can be verified on the public side to confirm inbound remittances.'}
                        </Typography>
                    </Box>
                </Stack>
            }
        />
    )
}
